import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { logger } from '@/utils/logger';
import { getOrganization } from '../api/org-api';
import type { Organization } from '../org-types';

type ProjectOrganizationBrandingStatus = 'idle' | 'loading' | 'ready' | 'error';

interface ProjectOrganizationBranding {
    status: ProjectOrganizationBrandingStatus;
    organization: Organization | null;
    logo: string | null;
    error: string | null;
}

interface LoadedProjectOrganizationBranding extends ProjectOrganizationBranding {
    orgId: string;
    userId: string;
}

const EMPTY_BRANDING: ProjectOrganizationBranding = {
    status: 'idle',
    organization: null,
    logo: null,
    error: null,
};

export function useProjectOrganizationBranding(): ProjectOrganizationBranding {
    const userId = useAppStore((state) => state.auth.user?.uid);
    const orgId = useAppStore((state) => state.cloud.orgId);
    const [loadedState, setLoadedState] = useState<LoadedProjectOrganizationBranding | null>(null);

    useEffect(() => {
        if (!userId || !orgId) {
            return;
        }

        const currentUserId = userId;
        const currentOrgId = orgId;
        let cancelled = false;

        async function loadOrganizationBranding() {
            try {
                const organization = await getOrganization(currentOrgId);
                if (cancelled) return;

                setLoadedState({
                    userId: currentUserId,
                    orgId: currentOrgId,
                    status: organization ? 'ready' : 'error',
                    organization,
                    logo: organization?.logo || null,
                    error: organization ? null : 'Current project organization was not found',
                });
            } catch (error: unknown) {
                logger.error('Failed to load project organization branding:', error);
                if (!cancelled) {
                    setLoadedState({
                        userId: currentUserId,
                        orgId: currentOrgId,
                        status: 'error',
                        organization: null,
                        logo: null,
                        error: 'Failed to load project organization branding',
                    });
                }
            }
        }

        void loadOrganizationBranding();

        return () => {
            cancelled = true;
        };
    }, [orgId, userId]);

    return useMemo(() => {
        if (!userId || !orgId) {
            return EMPTY_BRANDING;
        }

        if (!loadedState || loadedState.userId !== userId || loadedState.orgId !== orgId) {
            return {
                ...EMPTY_BRANDING,
                status: 'loading',
            };
        }

        return {
            status: loadedState.status,
            organization: loadedState.organization,
            logo: loadedState.logo,
            error: loadedState.error,
        };
    }, [loadedState, orgId, userId]);
}
